import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Briefcase, Check, ArrowRight } from "@phosphor-icons/react";
import { api, formatEURCompact } from "@/lib/api";
import { bosActionLabel } from "@/lib/bosActions";
import EmailRelanceButton from "./EmailRelanceButton";
import { track, USAGE_EVENTS } from "@/lib/usage";

/**
 * V4.28 — Bloc Suivi BOS dans Commando.
 *
 * Affiche uniquement les dossiers délégués au BOS dont la date de contrôle
 * hebdomadaire est arrivée. Safe = Silence : si liste vide, le bloc disparaît
 * (le parent ne nous rend pas, ou on retourne null).
 *
 * Action "BOS relancé" : optimistic — retire immédiatement de la liste.
 */

const STAGE_LABELS = {
  prospect: "Prospect",
  compromis: "Compromis",
  envoi_banque: "En banque",
  accord_banque: "Accord banque",
  accord_risque: "Accord risque",
  rdv_bancaire: "RDV bancaire",
  offre_editee: "Offre éditée",
  acte_authentique: "Acte authentique",
};

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
  } catch { return iso; }
}

function BosRow({ d, onFollowup }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const actionLabel = bosActionLabel(d.bos_next_action, d.bos_next_action_custom);

  const handleFollowup = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    // V4.36 — Track contrôle BOS effectué
    track(USAGE_EVENTS.COMMANDO_BOS_RELANCE, { dossier_id: d.id, etape: d.etape });
    try {
      await api.post(`/dossiers/${d.id}/bos/followup`);
      onFollowup(d.id);
    } catch {
      setBusy(false);
    }
  };

  return (
    <li
      className="rounded-xl p-3 border border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
      onClick={() => navigate(`/dossier/${d.id}`)}
      data-testid={`bos-controle-${d.id}`}
    >
      <div className="flex items-start gap-3">
        {/* Icone gauche */}
        <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-violet-100 text-violet-700 flex-shrink-0">
          <Briefcase size={16} weight="fill" />
        </div>

        {/* Centre */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-[13.5px] text-slate-900 truncate">
              {d.nom || d.client_name || "(Sans nom)"}
            </span>
            {actionLabel && (
              <span
                className="inline-flex items-center text-[10px] font-extrabold rounded px-1.5 py-0.5 bg-amber-100 text-amber-800 whitespace-nowrap"
                title="Action attendue du BOS"
                data-testid={`bos-action-${d.id}`}
              >
                {actionLabel}
              </span>
            )}
            {d.ca_net > 0 && (
              <span className="text-[11px] font-extrabold tabular-nums text-slate-700">
                {formatEURCompact(d.ca_net)}
              </span>
            )}
          </div>
          <div className="text-[10.5px] font-semibold text-slate-500 mt-0.5 leading-tight flex flex-wrap gap-x-2">
            {d.bos_name && <span className="text-violet-700">BOS : {d.bos_name}</span>}
            {d.etape && <span>· {STAGE_LABELS[d.etape] || d.etape}</span>}
            {d.rdv_bancaire_date && <span>· RDV banque {fmtDate(d.rdv_bancaire_date)}</span>}
          </div>
          <div className="text-[10.5px] font-semibold text-slate-500 mt-0.5">
            {d.bos_last_followup_date ? (
              <span>Dernière relance {fmtDate(d.bos_last_followup_date)}</span>
            ) : (
              <span className="text-amber-700">Aucune relance enregistrée</span>
            )}
            <span className="text-violet-700"> · Prochain contrôle {fmtDate(d.bos_next_followup_date)}</span>
          </div>
        </div>

        {/* CTAs droite */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* V4.35 — Bouton Email avec template BOS prérempli */}
          <EmailRelanceButton
            audience="bos"
            dossier={d}
            compact
            testid={`bos-email-${d.id}`}
          />
          <button
            type="button"
            onClick={handleFollowup}
            disabled={busy}
            className="px-2.5 py-1.5 rounded-lg bg-violet-600 text-white text-[11.5px] font-extrabold hover:bg-violet-700 disabled:opacity-60 transition-colors inline-flex items-center gap-1"
            title="Marquer 'BOS relancé' — disparaît jusqu'à lundi prochain"
            data-testid={`bos-followup-${d.id}`}
          >
            <Check size={11} weight="bold" />
            Relancé
          </button>
          <ArrowRight size={14} className="text-slate-300" />
        </div>
      </div>
    </li>
  );
}

export default function BosControles({ items = [] }) {
  // Optimistic local removal
  const [hidden, setHidden] = useState(new Set());
  const visible = items.filter((d) => !hidden.has(d.id));

  // Safe = Silence
  if (!visible.length) return null;

  const onFollowup = (id) => setHidden((prev) => new Set([...prev, id]));

  return (
    <section className="card-base p-4 mb-4" data-testid="commando-bos-controles">
      <div className="flex items-center gap-2 mb-3">
        <Briefcase size={16} weight="fill" color="var(--cf-accent)" />
        <h2 className="font-display text-base md:text-lg font-extrabold text-slate-900">
          Suivi BOS
        </h2>
        <span className="ml-auto px-2 py-0.5 rounded-full bg-violet-100 text-violet-800 text-[11px] font-extrabold tabular-nums">
          {visible.length}
        </span>
      </div>
      <ul className="space-y-2">
        {visible.map((d) => (
          <BosRow key={d.id} d={d} onFollowup={onFollowup} />
        ))}
      </ul>
      <p className="mt-3 text-[10.5px] text-slate-400 font-semibold leading-snug">
        Contrôles hebdomadaires des dossiers délégués au BOS. Un clic sur « Relancé » repousse au lundi suivant.
      </p>
    </section>
  );
}
